import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, errMsg, fileUrl } from "@/lib/api";

export const Panel = ({ open, onClose, title, children, wide }) => (
  <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
    <SheetContent className={`w-full overflow-y-auto bg-na-bg ${wide ? "sm:max-w-3xl" : "sm:max-w-xl"}`} data-testid="admin-panel">
      <SheetHeader><SheetTitle className="font-serif text-2xl font-normal">{title}</SheetTitle></SheetHeader>
      <div className="mt-4">{children}</div>
    </SheetContent>
  </Sheet>
);

export const H = ({ children, actions }) => (
  <div className="mb-6 flex flex-wrap items-center justify-between gap-3"><h1 className="text-3xl sm:text-4xl">{children}</h1><div className="flex flex-wrap gap-2">{actions}</div></div>
);

export function Table({ cols, rows, onRow, testid = "admin-table" }) {
  return (
    <div className="overflow-x-auto rounded-[22px] border border-na-line bg-white">
      <table className="w-full min-w-[640px] text-left text-sm" data-testid={testid}>
        <thead className="bg-na-sage2 text-xs uppercase tracking-wider text-na-ink2"><tr>{cols.map((c) => <th key={c[0]} className="px-4 py-3 font-semibold">{c[1]}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} onClick={() => onRow?.(r)} className={`border-t border-na-line ${onRow ? "cursor-pointer hover:bg-na-sage2/50" : ""}`} data-testid={`row-${r.id || i}`}>
              {cols.map((c) => <td key={c[0]} className="px-4 py-3">{c[2] ? c[2](r) : String(r[c[0]] ?? "-")}</td>)}
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={cols.length} className="px-4 py-8 text-center text-na-ink2">Nada por aqui.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function UploadBtn({ onDone, label = "Enviar arquivo", accept = "image/*,application/pdf" }) {
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { const r = await api.post("/admin/upload", fd); onDone(r.data.url); toast.success("Arquivo enviado"); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <label className="btn-line cursor-pointer px-4 py-2 text-xs"><Upload className="h-4 w-4" />{busy ? "Enviando..." : label}<input type="file" accept={accept} className="hidden" onChange={up} data-testid="upload-input" /></label>
  );
}

export function Field({ f, value, onChange }) {
  const id = `fld-${f.k}`;
  const common = { id, "data-testid": `field-${f.k}` };
  let input;
  if (f.type === "textarea") input = <textarea {...common} rows={f.rows || 4} className="input-na" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  else if (f.type === "bool") return <label className="flex items-center gap-2 text-sm"><input type="checkbox" {...common} checked={!!value} onChange={(e) => onChange(e.target.checked)} />{f.label}</label>;
  else if (f.type === "select") input = <select {...common} className="input-na" value={value ?? ""} onChange={(e) => onChange(e.target.value)}><option value="">-</option>{f.options.map((o) => <option key={o[0] ?? o} value={o[0] ?? o}>{o[1] ?? o}</option>)}</select>;
  else if (f.type === "lines") input = <textarea {...common} rows={f.rows || 4} className="input-na" value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))} onBlur={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} />;
  else if (f.type === "json") input = <JsonInput {...common} value={value} onChange={onChange} />;
  else if (f.type === "image") input = <div className="flex items-center gap-3">{value && <img src={fileUrl(value)} alt="" className="h-14 w-14 rounded-xl object-cover" />}<input {...common} className="input-na" value={value ?? ""} onChange={(e) => onChange(e.target.value)} /><UploadBtn onDone={onChange} label="Enviar" /></div>;
  else input = <input {...common} type={f.type || "text"} step="any" className="input-na" value={value ?? ""} onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} />;
  return <div className={f.wide ? "sm:col-span-2" : ""}><label htmlFor={id} className="label-na">{f.label}</label>{input}{f.hint && <p className="mt-1 text-xs text-na-ink2">{f.hint}</p>}</div>;
}

function JsonInput({ value, onChange, ...rest }) {
  const [txt, setTxt] = useState(JSON.stringify(value ?? null, null, 2));
  const [bad, setBad] = useState(false);
  return <><textarea {...rest} rows={8} className={`input-na font-mono text-xs ${bad ? "border-red-500" : ""}`} value={txt} onChange={(e) => { setTxt(e.target.value); try { onChange(JSON.parse(e.target.value)); setBad(false); } catch { setBad(true); } }} />{bad && <p className="err-na">JSON inválido</p>}</>;
}

export function Form({ fields, data, setData }) {
  return <div className="grid gap-4 sm:grid-cols-2">{fields.map((f) => <Field key={f.k} f={f} value={data[f.k]} onChange={(v) => setData((d) => ({ ...d, [f.k]: v }))} />)}</div>;
}

export function Crud({ endpoint, title, fields, cols, defaults = {}, testid, extra }) {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get(`/admin/${endpoint}`).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [endpoint]); // eslint-disable-line
  const save = async () => {
    try {
      if (edit.id) await api.put(`/admin/${endpoint}/${edit.id}`, edit);
      else await api.post(`/admin/${endpoint}`, edit);
      toast.success("Salvo");
      setEdit(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm("Apagar este registro?")) return;
    await api.delete(`/admin/${endpoint}/${edit.id}`);
    setEdit(null);
    load();
  };
  return (
    <div data-testid={testid || `crud-${endpoint}`}>
      <div className="mb-4 flex items-center justify-between"><h2 className="font-serif text-2xl">{title}</h2><button className="btn-na px-4 py-2" onClick={() => setEdit({ ...defaults })} data-testid={`new-${endpoint}`}>Novo</button></div>
      <Table cols={cols} rows={rows} onRow={(r) => setEdit({ ...r })} testid={`table-${endpoint}`} />
      <Panel open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? "Editar" : "Novo"} wide>
        {edit && <><Form fields={fields} data={edit} setData={setEdit} />{extra?.(edit, setEdit, load)}
          <div className="mt-6 flex gap-2"><button className="btn-na" onClick={save} data-testid={`save-${endpoint}`}>Salvar</button>{edit.id && <button className="btn-line text-red-700" onClick={del} data-testid={`delete-${endpoint}`}>Apagar</button>}</div></>}
      </Panel>
    </div>
  );
}

export const Stat = ({ label, value, testid, tone }) => (
  <div className={`rounded-[22px] border border-na-line p-5 ${tone || "bg-white"}`} data-testid={testid}><p className="text-xs font-semibold uppercase tracking-wider text-na-ink2">{label}</p><p className="mt-2 font-serif text-3xl">{value}</p></div>
);

export const Tabs = ({ tabs, value, onChange }) => (
  <div className="no-scrollbar mb-6 flex gap-2 overflow-x-auto">{tabs.map(([k, l]) => <button key={k} onClick={() => onChange(k)} className={`chip ${value === k ? "chip-on" : ""}`} data-testid={`subtab-${k}`}>{l}</button>)}</div>
);
